import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { GlossaryDialog } from "./Explain";
import { Stack, GearSix, SignOut, Gauge, Brain, Archive, GlobeHemisphereEast, ChartBar, Path, RocketLaunch, Broadcast, PresentationChart, Buildings, BookOpen, Question, Briefcase, CaretDown, CaretRight } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { GmariBadge } from "./GmariBadge";

/* Workflow-oriented IA: KNOW → ASSESS → PLAN → EXECUTE → MONITOR.
   Every legacy route stays alive — items below are the workspace tabs. */
const GROUPS = [
  { section: "Home" },
  { id: "readiness", label: "Global Readiness", icon: Gauge, to: "/console" },
  { section: "Knowledge" },
  { id: "hub", label: "MAI Knowledge Hub", icon: Brain, items: [
    ["Overview & Upload", "/console/knowledge"],
  ]},
  { id: "evidence", label: "Evidence", icon: Archive, items: [
    ["Evidence Library", "/console/evidence-library"],
    ["Evidence Review", "/console/evidence-review"],
    ["Evidence Insights", "/console/evidence-insights"],
  ]},
  { section: "Assess" },
  { id: "cases", label: "Access Cases", icon: Briefcase, to: "/console/access-cases" },
  { id: "intel", label: "Market Intelligence", icon: GlobeHemisphereEast, items: [
    ["Countries", "/console/intelligence"], ["Epidemiology & Demand", "/console/epidemiology"],
    ["Competitors & Precedents", "/console/competitive"],
  ]},
  { id: "gaps", label: "Gap & Reuse Analysis", icon: ChartBar, items: [
    ["Evidence Gaps", "/console/gaps"], ["Reusability", "/console/reusability"],
  ]},
  { section: "Plan" },
  { id: "strategy", label: "Strategy Workspace", icon: Path, items: [
    ["Strategy Journey", "/console/journey"], ["Market Prioritization", "/console/prioritization"],
    ["Decision Intelligence", "/console/decisions"], ["Scenario Simulator", "/console/whatif"],
    ["Recommendation Planner", "/console/planner"],
  ]},
  { id: "submission", label: "Submission & Launch", icon: RocketLaunch, items: [
    ["Global Value Dossier", "/console/gvd"], ["Submission Dossier", "/console/dossier"],
    ["Submission Planner", "/console/submissions"], ["Launch Roadmap", "/console/roadmap"],
    ["Readiness Report", "/console/report"],
  ]},
  { section: "Monitor" },
  { id: "monitor", label: "Intelligence Monitor", icon: Broadcast, items: [
    ["Signals & Changes", "/console/monitor"], ["Sources & Currency", "/console/intel-monitor"],
  ]},
  { id: "executive", label: "Executive Insights", icon: PresentationChart, to: "/console/executive" },
  { section: "Administration" },
  { id: "admin", label: "Platform Administration", icon: Buildings, items: [
    ["Platform & Users", "/console/platform"], ["Calculation Reconciliation", "/console/reconciliation"],
    ["GMARI™ Methodology", "/console/methodology"], ["Data Dictionary", "/console/dictionary"],
    ["Website Analytics", "/console/analytics"],
  ]},
];

export const Sidebar = () => {
  const { products, productId, setProductId } = useProduct();
  const { user, logout } = useAuth();
  const { pathname, search } = useLocation();
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [open, setOpen] = useState({});

  const isActive = (to) => {
    const [p, qs] = to.split("?");
    if (p === "/console") return pathname === "/console" || pathname === "/console/";
    if (!pathname.startsWith(p)) return false;
    if (qs) return search.includes(qs);
    if (p === "/console/queries") return !search.includes("tab=learning");
    return true;
  };
  const groupActive = (g) => (g.to && isActive(g.to)) || (g.items || []).some(([, to]) => isActive(to));

  useEffect(() => {
    const active = GROUPS.find((g) => g.items && groupActive(g));
    if (active) setOpen((o) => ({ ...o, [active.id]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  return (
  <aside data-testid="sidebar" className="hidden lg:flex flex-col w-64 shrink-0 border-r border-slate-200 bg-white min-h-screen sticky top-0">
    <div className="px-6 py-6 border-b border-slate-200">
      <div className="flex items-center gap-2">
        <Stack size={22} weight="fill" className="text-slate-900" />
        <span className="font-heading font-extrabold text-lg tracking-tight">MAI MATRIX<span className="align-super text-[9px]">™</span></span>
      </div>
      <p className="overline-label text-slate-400 mt-2">Global Market Access Intelligence Platform</p>
    </div>
    <div className="px-3 pt-4">
      <p className="overline-label text-slate-400 px-3 mb-1.5">Product</p>
      <Select value={productId} onValueChange={setProductId}>
        <SelectTrigger data-testid="sidebar-product-selector" className="w-full bg-white border-slate-200 rounded-md text-sm">
          <SelectValue placeholder="Select product" />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {products.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`sidebar-product-${p.id}`}>{p.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Link to="/console/workspaces" data-testid="manage-products-link"
        className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-900 px-3 pt-2">
        <GearSix size={12} /> Manage products…
      </Link>
      <div className="px-3 pt-2.5">
        <GmariBadge full />
      </div>
    </div>
    <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
      {GROUPS.map((g) => {
        if (g.section) {
          return <p key={g.section} className="overline-label text-slate-400 px-3 pt-4 pb-1">{g.section}</p>;
        }
        const testId = `nav-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
        const active = groupActive(g);
        if (g.to && !g.items) {
          return (
            <Link key={g.id} to={g.to} data-testid={testId}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md ${active ? "bg-slate-900 text-white font-medium" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
              <g.icon size={18} /><span className="flex-1">{g.label}</span>
            </Link>
          );
        }
        const expanded = !!open[g.id];
        return (
          <div key={g.id}>
            <button data-testid={testId} onClick={() => setOpen((o) => ({ ...o, [g.id]: !expanded }))}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-left ${active ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
              <g.icon size={18} /><span className="flex-1">{g.label}</span>
              {expanded ? <CaretDown size={12} className="text-slate-400" /> : <CaretRight size={12} className="text-slate-400" />}
            </button>
            {expanded && (
              <div className="ml-5 border-l border-slate-200 pl-2 py-1 space-y-0.5">
                {g.items.map(([label, to]) => (
                  <Link key={to} to={to} data-testid={`nav-sub-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`block px-3 py-1.5 text-[13px] rounded-md ${isActive(to) ? "bg-slate-900 text-white font-medium" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"}`}>
                    {label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <button data-testid="glossary-btn" onClick={() => setGlossaryOpen(true)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900">
        <BookOpen size={18} /><span className="flex-1">Market Access Glossary</span>
      </button>
    </nav>
    <div className="px-6 py-5 border-t border-slate-200">
      {user && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p data-testid="sidebar-user-name" className="text-xs font-semibold text-slate-800 truncate">{user.name}</p>
            <p className="text-[10px] text-slate-400 truncate">{user.email}{user.role === "admin" ? " · admin" : ""}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout} title="Sign out"
            className="shrink-0 text-slate-400 hover:text-red-600 p-1.5">
            <SignOut size={16} />
          </button>
        </div>
      )}
      <p className="text-[11px] text-slate-400 leading-relaxed">MAI MATRIX™ — Global Market Access Intelligence Platform</p>
    </div>
    <GlossaryDialog open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
  </aside>
  );
};
